export interface RWQuestion {
  id: number;
  passage: string;
  question: string;
  choices: string[];
  correctAnswer: number;
  explanation: string;
  subtopic: string;
}

export const craftStructureQuestions: RWQuestion[] = [
  {
    id: 1,
    passage: "The term 'sustainability' has become ubiquitous in corporate marketing, appearing on everything from coffee cups to car advertisements. However, this widespread adoption has led to what critics call 'greenwashing'—the practice of making misleading claims about environmental benefits. When every product claims to be 'eco-friendly' or 'sustainable,' the terms lose their meaning, making it difficult for consumers to identify genuinely environmentally responsible options.",
    question: "Which choice best describes the function of the sentence beginning with 'However' in the text?",
    choices: [
      "It introduces a problem that the rest of the passage will explore.",
      "It provides a solution to the issue discussed in the previous sentence.",
      "It offers evidence supporting the claim made in the following sentence.",
      "It presents a counterargument to the passage's main point."
    ],
    correctAnswer: 0,
    explanation: "The 'However' sentence introduces the concept of 'greenwashing' as a problem resulting from the widespread use of sustainability terms. The rest of the passage then explores this problem and its consequences for consumers.",
    subtopic: "Text Structure and Purpose"
  },
  {
    id: 2,
    passage: "Archaeologists have long debated the purpose of Stonehenge, the prehistoric monument in England. Some scholars argue it served as an astronomical observatory, pointing to alignments with solstice sunrises. Others suggest it was a healing temple, noting the presence of human remains showing signs of illness and injury. A third theory proposes it functioned as a ceremonial gathering place for ancient communities. Recent excavations have uncovered evidence supporting elements of all three interpretations, suggesting Stonehenge may have served multiple purposes throughout its long history.",
    question: "Which choice best describes the overall structure of the text?",
    choices: [
      "It presents a mystery, offers several competing theories, and suggests a synthesis.",
      "It describes a historical site, critiques previous research, and proposes a new theory.",
      "It introduces a debate, provides evidence for one side, and dismisses alternatives.",
      "It outlines a problem, explains its causes, and recommends solutions."
    ],
    correctAnswer: 0,
    explanation: "The passage presents the mystery of Stonehenge's purpose, describes three competing theories, and concludes by suggesting these theories can be synthesized—that Stonehenge served multiple purposes.",
    subtopic: "Text Structure and Purpose"
  },
  {
    id: 3,
    passage: "In her novel Beloved, Toni Morrison employs a non-linear narrative structure, moving fluidly between past and present. This technique mirrors the psychological experience of trauma, where memories intrude unpredictably into the present moment. By disrupting chronological storytelling, Morrison forces readers to piece together the narrative much as her characters must reconstruct their fragmented histories.",
    question: "According to the text, what is the primary purpose of Morrison's non-linear narrative technique?",
    choices: [
      "To confuse readers and make the novel more challenging",
      "To reflect the psychological experience of trauma",
      "To demonstrate the author's technical skill",
      "To distinguish her work from other contemporary novels"
    ],
    correctAnswer: 1,
    explanation: "The passage explicitly states that the non-linear structure 'mirrors the psychological experience of trauma, where memories intrude unpredictably into the present moment.' This is identified as the primary purpose.",
    subtopic: "Text Structure and Purpose"
  },
  {
    id: 4,
    passage: "The introduction of wolves to Yellowstone National Park in 1995 triggered a cascade of ecological changes. Elk populations, no longer free to graze without fear, altered their behavior and moved to different areas. This allowed vegetation in valleys to recover, which stabilized riverbanks and reduced erosion. The returning vegetation attracted beavers, whose dams created wetland habitats for numerous species. This phenomenon, where a single species dramatically affects an entire ecosystem, is known as a 'trophic cascade.'",
    question: "The text uses the example of Yellowstone wolves primarily to",
    choices: [
      "argue for the reintroduction of predators to all national parks.",
      "illustrate the concept of a trophic cascade.",
      "explain why elk populations declined in the 1990s.",
      "describe the history of wildlife management in Yellowstone."
    ],
    correctAnswer: 1,
    explanation: "The passage concludes by defining 'trophic cascade' and explicitly states that the wolf example demonstrates 'where a single species dramatically affects an entire ecosystem.' The example serves to illustrate this concept.",
    subtopic: "Text Structure and Purpose"
  },
  {
    id: 5,
    passage: "The scientist's methodology was rigorous: she conducted multiple trials, controlled for confounding variables, and subjected her findings to peer review before publication.",
    question: "As used in the text, what does 'rigorous' most nearly mean?",
    choices: [
      "Difficult or challenging",
      "Strict or harsh",
      "Thorough and precise",
      "Time-consuming"
    ],
    correctAnswer: 2,
    explanation: "In this context, 'rigorous' means thorough and precise. The description of multiple trials, controlled variables, and peer review all indicate a careful, exacting approach to scientific methodology.",
    subtopic: "Words in Context"
  },
  {
    id: 6,
    passage: "The politician's rhetoric was incendiary, inflaming tensions between opposing groups rather than seeking common ground.",
    question: "As used in the text, what does 'incendiary' most nearly mean?",
    choices: [
      "Related to fire or burning",
      "Provocative or inflammatory",
      "Dishonest or deceptive",
      "Loud or aggressive"
    ],
    correctAnswer: 1,
    explanation: "While 'incendiary' literally relates to fire, in this context it means provocative or inflammatory. The phrase 'inflaming tensions' reinforces this figurative meaning of stirring up conflict.",
    subtopic: "Words in Context"
  },
  {
    id: 7,
    passage: "The artist's work was derivative, borrowing heavily from earlier movements without adding original insight or innovation.",
    question: "As used in the text, what does 'derivative' most nearly mean?",
    choices: [
      "Mathematical or calculated",
      "Unoriginal or imitative",
      "Complex or sophisticated",
      "Controversial or provocative"
    ],
    correctAnswer: 1,
    explanation: "In this context, 'derivative' means unoriginal or imitative. The phrase 'borrowing heavily from earlier movements without adding original insight' clarifies that the work lacks originality.",
    subtopic: "Words in Context"
  },
  {
    id: 8,
    passage: "The committee's report was exhaustive, covering every aspect of the proposed policy from its economic implications to its environmental impact and long-term social consequences.",
    question: "As used in the text, what does 'exhaustive' most nearly mean?",
    choices: [
      "Tiring or draining",
      "Comprehensive and thorough",
      "Critical or negative",
      "Preliminary or incomplete"
    ],
    correctAnswer: 1,
    explanation: "In this context, 'exhaustive' means comprehensive and thorough. The passage describes the report as covering 'every aspect' of the policy, indicating completeness rather than fatigue.",
    subtopic: "Words in Context"
  },
  {
    id: 9,
    passage: "While discussing the impact of social media on democracy, the author ______ that these platforms have both democratized information access and enabled the spread of misinformation.",
    question: "Which choice completes the text with the most logical and precise word or phrase?",
    choices: [
      "denies",
      "acknowledges",
      "questions",
      "dismisses"
    ],
    correctAnswer: 1,
    explanation: "'Acknowledges' is the most logical choice because the sentence presents two contrasting but coexisting realities about social media. The author is recognizing both positive and negative aspects simultaneously.",
    subtopic: "Words in Context"
  },
  {
    id: 10,
    passage: "The new policy aims to ______ economic growth while simultaneously protecting environmental resources—a balance that has proven difficult to achieve.",
    question: "Which choice completes the text with the most logical and precise word or phrase?",
    choices: [
      "prevent",
      "promote",
      "ignore",
      "question"
    ],
    correctAnswer: 1,
    explanation: "'Promote' is the correct choice because the policy aims to encourage economic growth while also protecting the environment. The phrase 'balance that has proven difficult' indicates the policy is trying to achieve both goals.",
    subtopic: "Words in Context"
  },
  {
    id: 11,
    passage: "In The Great Gatsby, F. Scott Fitzgerald uses the green light at the end of Daisy's dock as a recurring symbol. The light represents Gatsby's hopes and dreams, particularly his desire to recapture the past and reunite with Daisy. Each time Gatsby gazes at the light, readers are reminded of the distance—both physical and emotional—between Gatsby and his aspirations. The symbol's power lies in its ambiguity: the green light is simultaneously a beacon of hope and a reminder of unattainability.",
    question: "Based on the text, the green light functions as a symbol that primarily represents",
    choices: [
      "the wealth and materialism of the 1920s.",
      "Gatsby's longing for an idealized past.",
      "the geographical distance between Gatsby and Daisy.",
      "the environmental concerns of the era."
    ],
    correctAnswer: 1,
    explanation: "The passage explicitly states the light 'represents Gatsby's hopes and dreams, particularly his desire to recapture the past and reunite with Daisy.' This longing for an idealized past is identified as the symbol's primary meaning.",
    subtopic: "Text Structure and Purpose"
  },
  {
    id: 12,
    passage: "Text 1: In his 1849 essay 'Civil Disobedience,' Henry David Thoreau argues that individuals have a moral obligation to resist unjust laws through peaceful non-compliance. Thoreau writes: 'Unjust laws exist: shall we be content to obey them, or shall we endeavor to amend them, and obey them until we have succeeded, or shall we transgress them at once?'\n\nText 2: Martin Luther King Jr.'s 1963 'Letter from Birmingham Jail' echoes Thoreau's philosophy, arguing that 'one has a moral responsibility to disobey unjust laws.' King distinguishes between just and unjust laws, stating that 'a just law is a man-made code that squares with the moral law,' while an unjust law 'is out of harmony with the moral law.'",
    question: "Based on the texts, how would King most likely respond to Thoreau's question about unjust laws?",
    choices: [
      "By arguing that all laws must be obeyed regardless of their justice",
      "By agreeing that unjust laws should be transgressed immediately",
      "By suggesting that legal reform is preferable to civil disobedience",
      "By claiming that the distinction between just and unjust laws is unclear"
    ],
    correctAnswer: 1,
    explanation: "King explicitly states that 'one has a moral responsibility to disobey unjust laws,' which aligns with Thoreau's suggestion to 'transgress them at once.' Both advocate for immediate non-compliance with unjust laws.",
    subtopic: "Cross-Text Connections"
  },
  {
    id: 13,
    passage: "Text 1: Rachel Carson's 1962 book Silent Spring warned that widespread pesticide use was causing catastrophic environmental damage. Carson wrote: 'The most alarming of all man's assaults upon the environment is the contamination of air, earth, rivers, and sea with dangerous and even lethal materials.'\n\nText 2: In response to Carson's work, chemical industry representatives argued that pesticides were essential for agricultural productivity and that Carson exaggerated the risks. They claimed that without chemical pest control, crop yields would plummet, leading to food shortages and economic hardship.",
    question: "Which choice best describes the relationship between Text 1 and Text 2?",
    choices: [
      "Text 2 provides additional evidence supporting Text 1's claims.",
      "Text 2 presents a counterargument to Text 1's position.",
      "Text 2 offers a historical context for Text 1's publication.",
      "Text 2 clarifies technical details mentioned in Text 1."
    ],
    correctAnswer: 1,
    explanation: "Text 2 presents the chemical industry's response, which argues against Carson's position by claiming pesticides are necessary and that she exaggerated risks. This is clearly a counterargument to Text 1's warnings.",
    subtopic: "Cross-Text Connections"
  },
  {
    id: 14,
    passage: "Text 1: Behavioral economist Dan Ariely's research demonstrates that humans are predictably irrational in their decision-making. In experiments, people consistently make choices that contradict traditional economic models of rational self-interest, influenced by factors like social norms, emotional states, and cognitive biases.\n\nText 2: Traditional economic theory assumes that individuals are rational actors who make decisions by weighing costs and benefits to maximize their utility. This model, known as 'homo economicus,' has been the foundation of economic analysis for centuries and continues to inform policy decisions worldwide.",
    question: "Based on the texts, Ariely's research most directly challenges which assumption of Text 2?",
    choices: [
      "That economic analysis has been conducted for centuries",
      "That economic models inform policy decisions",
      "That individuals make rational, self-interested decisions",
      "That people consider costs and benefits when making choices"
    ],
    correctAnswer: 2,
    explanation: "Ariely's research shows that 'humans are predictably irrational' and make choices that 'contradict traditional economic models of rational self-interest.' This directly challenges Text 2's assumption that individuals are 'rational actors.'",
    subtopic: "Cross-Text Connections"
  },
  {
    id: 15,
    passage: "The committee's decision to postpone the vote was ______, given the absence of several key members whose input would be crucial to reaching a fair consensus.",
    question: "Which choice completes the text with the most logical and precise word or phrase?",
    choices: [
      "arbitrary",
      "premature",
      "prudent",
      "controversial"
    ],
    correctAnswer: 2,
    explanation: "'Prudent' (meaning wise or sensible) is the best choice. The sentence explains that key members were absent and their input was crucial, making postponement a wise decision. The word 'given' signals that the decision was justified by the circumstances.",
    subtopic: "Words in Context"
  },
  {
    id: 16,
    passage: "Despite initial skepticism from critics, the experimental theater production proved to be ______, attracting sold-out audiences and receiving widespread acclaim.",
    question: "Which choice completes the text with the most logical and precise word or phrase?",
    choices: [
      "a failure",
      "controversial",
      "a triumph",
      "conventional"
    ],
    correctAnswer: 2,
    explanation: "'A triumph' is correct because the sentence contrasts initial skepticism with positive outcomes (sold-out audiences, widespread acclaim). The word 'Despite' signals this contrast, and 'triumph' best captures the success described.",
    subtopic: "Words in Context"
  },
  {
    id: 17,
    passage: "The historian's account of the revolution was criticized for being too ______, focusing exclusively on political leaders while ignoring the experiences of ordinary citizens who participated in the uprising.",
    question: "Which choice completes the text with the most logical and precise word or phrase?",
    choices: [
      "comprehensive",
      "elitist",
      "objective",
      "detailed"
    ],
    correctAnswer: 1,
    explanation: "'Elitist' is the best choice because the criticism is that the account focused only on political leaders while ignoring ordinary citizens. This represents an elitist perspective that privileges the powerful over common people.",
    subtopic: "Words in Context"
  },
  {
    id: 18,
    passage: "In George Orwell's 1984, the concept of 'doublethink'—the ability to hold two contradictory beliefs simultaneously—serves as a critique of totalitarian ideology. The Party uses doublethink to maintain power, requiring citizens to accept obvious falsehoods as truth. For example, the Party's slogans 'War is Peace,' 'Freedom is Slavery,' and 'Ignorance is Strength' embody this principle. By forcing people to embrace logical contradictions, the Party destroys their capacity for independent thought and critical reasoning.",
    question: "According to the text, what is the primary function of 'doublethink' in Orwell's novel?",
    choices: [
      "To demonstrate the complexity of human psychology",
      "To illustrate the Party's method of controlling thought",
      "To show how language evolves under totalitarianism",
      "To explain why citizens support the Party's policies"
    ],
    correctAnswer: 1,
    explanation: "The passage states that 'The Party uses doublethink to maintain power' and that 'By forcing people to embrace logical contradictions, the Party destroys their capacity for independent thought.' This clearly identifies thought control as doublethink's primary function.",
    subtopic: "Text Structure and Purpose"
  },
  {
    id: 19,
    passage: "The Renaissance concept of 'sprezzatura,' articulated by Baldassare Castiglione in The Book of the Courtier (1528), refers to a studied nonchalance—the ability to perform difficult tasks with apparent ease. Castiglione argued that the ideal courtier should conceal the effort behind their accomplishments, making skill appear natural rather than labored. This aesthetic principle influenced not only Renaissance court culture but also modern notions of effortless style and 'cool' demeanor.",
    question: "Based on the text, 'sprezzatura' primarily involves",
    choices: [
      "developing exceptional skills through intensive practice.",
      "making difficult accomplishments appear effortless.",
      "rejecting formal training in favor of natural talent.",
      "demonstrating superiority over less skilled individuals."
    ],
    correctAnswer: 1,
    explanation: "The passage defines sprezzatura as 'the ability to perform difficult tasks with apparent ease' and states that practitioners should 'conceal the effort behind their accomplishments, making skill appear natural.' This is about making accomplishments appear effortless.",
    subtopic: "Text Structure and Purpose"
  },
  {
    id: 20,
    passage: "Text 1: Philosopher John Rawls's 'veil of ignorance' thought experiment asks us to imagine designing a just society without knowing what position we would occupy in it. Behind this veil, we wouldn't know our race, gender, wealth, or abilities. Rawls argues that rational people in this position would choose principles ensuring fair treatment for all.\n\nText 2: Economist Friedrich Hayek criticized such theoretical approaches to justice, arguing that society is too complex to be designed according to abstract principles. Hayek believed that just social orders emerge spontaneously from individuals pursuing their own interests within a framework of general rules, not from centralized planning based on philosophical ideals.",
    question: "Based on the texts, Hayek would most likely argue that Rawls's approach",
    choices: [
      "correctly identifies the principles of a just society.",
      "overlooks the complexity of real social systems.",
      "should be implemented through democratic processes.",
      "fails to consider the perspective of disadvantaged groups."
    ],
    correctAnswer: 1,
    explanation: "Text 2 states that Hayek 'argued that society is too complex to be designed according to abstract principles,' which directly applies to Rawls's theoretical approach. Hayek believed just orders emerge spontaneously, not from philosophical design.",
    subtopic: "Cross-Text Connections"
  },
  {
    id: 21,
    passage: "The author's prose style in the novel is remarkably ______, stripping away ornamental language to reveal the raw emotional core of each scene with minimal but precisely chosen words.",
    question: "Which choice completes the text with the most logical and precise word or phrase?",
    choices: [
      "verbose",
      "austere",
      "whimsical",
      "convoluted"
    ],
    correctAnswer: 1,
    explanation: "'Austere' means severely simple and unadorned, which perfectly matches the description of 'stripping away ornamental language' and using 'minimal but precisely chosen words.' The other choices contradict this description.",
    subtopic: "Words in Context"
  },
  {
    id: 22,
    passage: "Text 1: Proponents of universal basic income (UBI) argue that providing every citizen with a guaranteed monthly payment would eliminate poverty, reduce bureaucratic inefficiency in welfare programs, and give workers the freedom to pursue meaningful work rather than accepting any available job out of financial desperation.\n\nText 2: Critics of universal basic income contend that unconditional cash transfers would reduce the incentive to work, leading to labor shortages and reduced economic productivity. They also argue that the cost of funding UBI would require significant tax increases that could harm economic growth.",
    question: "Which statement best describes a point on which the two texts would most likely agree?",
    choices: [
      "Universal basic income would eliminate poverty.",
      "Universal basic income would have significant economic consequences.",
      "Universal basic income would reduce bureaucratic inefficiency.",
      "Universal basic income would harm economic growth."
    ],
    correctAnswer: 1,
    explanation: "Both texts acknowledge that UBI would have major economic effects—Passage 1 positively (eliminating poverty, freeing workers) and Passage 2 negatively (reducing work incentives, requiring tax increases). Both agree on the significance of the economic impact.",
    subtopic: "Cross-Text Connections"
  },
  {
    id: 23,
    passage: "The documentary opens with sweeping aerial footage of the Amazon rainforest, then cuts abruptly to images of deforestation and burning. This jarring juxtaposition is not accidental. The filmmaker uses visual contrast as her primary rhetorical strategy, forcing viewers to confront the gap between what exists and what is being destroyed. By refusing to narrate these images, she allows the visual evidence to speak for itself.",
    question: "According to the text, why does the filmmaker avoid narrating the images?",
    choices: [
      "To reduce the film's production costs",
      "To allow the visual evidence to make its own argument",
      "To focus attention on the aerial footage of the rainforest",
      "To avoid taking a political position on deforestation"
    ],
    correctAnswer: 1,
    explanation: "The passage explicitly states that 'By refusing to narrate these images, she allows the visual evidence to speak for itself.' The filmmaker's choice is deliberate—to let the images make the argument without verbal mediation.",
    subtopic: "Text Structure and Purpose"
  },
  {
    id: 24,
    passage: "The philosopher's argument was ______ at first glance, appearing to offer a straightforward solution to the problem of free will, but closer examination revealed numerous unresolved contradictions at its core.",
    question: "Which choice completes the text with the most logical and precise word or phrase?",
    choices: [
      "incoherent",
      "deceptively elegant",
      "obviously flawed",
      "widely accepted"
    ],
    correctAnswer: 1,
    explanation: "'Deceptively elegant' is correct because the sentence describes something that appears straightforward and appealing at first but contains hidden flaws upon closer examination. 'Deceptively' captures the misleading initial impression.",
    subtopic: "Words in Context"
  },
  {
    id: 25,
    passage: "The essay begins by establishing common ground with readers who support economic growth, then gradually introduces evidence that unchecked growth has environmental costs, and finally proposes a framework for sustainable development that satisfies both economic and environmental goals. Throughout, the author anticipates and addresses potential objections before they can be raised.",
    question: "Which choice best describes the rhetorical strategy of the essay as described in the passage?",
    choices: [
      "It uses emotional appeals to persuade readers to abandon economic growth.",
      "It builds toward a compromise position by first acknowledging opposing views.",
      "It presents a series of unrelated arguments about environmental policy.",
      "It relies primarily on statistical evidence to support its claims."
    ],
    correctAnswer: 1,
    explanation: "The passage describes an essay that starts by agreeing with growth supporters, introduces environmental concerns, and proposes a compromise framework—all while addressing objections. This is a classic strategy of building toward a middle-ground position.",
    subtopic: "Text Structure and Purpose"
  },
  {
    id: 26,
    passage: "Text 1: The widespread adoption of remote work during the pandemic demonstrated that many jobs can be performed effectively outside traditional office settings. Employees reported higher satisfaction, reduced commuting stress, and improved work-life balance. Companies that embraced remote work retained talent more effectively than those that did not.\n\nText 2: While remote work offers flexibility, it also creates significant challenges for collaboration, mentorship, and organizational culture. Junior employees in particular miss out on the informal learning that occurs through proximity to experienced colleagues. The long-term effects on career development and team cohesion remain concerning.",
    question: "How does Text 2 respond to the evidence presented in Text 1?",
    choices: [
      "By disputing the claim that employees reported higher satisfaction",
      "By acknowledging remote work's benefits while raising concerns about its costs",
      "By arguing that remote work should be permanently eliminated",
      "By providing additional data supporting Text 1's conclusions"
    ],
    correctAnswer: 1,
    explanation: "Text 2 begins with 'While remote work offers flexibility,' acknowledging the benefits described in Text 1, but then raises concerns about collaboration, mentorship, and career development. This is a classic concession-and-rebuttal structure.",
    subtopic: "Cross-Text Connections"
  },
  {
    id: 27,
    passage: "The biographer's treatment of her subject was notably ______, presenting both the statesman's remarkable achievements and his well-documented moral failures with equal candor, neither lionizing nor condemning him.",
    question: "Which choice completes the text with the most logical and precise word or phrase?",
    choices: [
      "hagiographic",
      "evenhanded",
      "dismissive",
      "sensationalized"
    ],
    correctAnswer: 1,
    explanation: "'Evenhanded' means fair and impartial, which matches the description of presenting both achievements and failures 'with equal candor, neither lionizing nor condemning.' The other choices suggest bias in one direction or another.",
    subtopic: "Words in Context"
  },
  {
    id: 28,
    passage: "The opening paragraph of the novel establishes the narrator as unreliable through subtle but deliberate signals: the narrator contradicts herself twice within the first page, describes events with suspicious precision about details she could not have witnessed, and refers to her own memory as 'selective.' These cues invite readers to question everything that follows.",
    question: "According to the text, what is the primary purpose of the opening paragraph's techniques?",
    choices: [
      "To establish the setting and time period of the novel",
      "To signal to readers that the narrator's account cannot be fully trusted",
      "To introduce the novel's central conflict",
      "To demonstrate the author's skill with language"
    ],
    correctAnswer: 1,
    explanation: "The passage states that the cues 'invite readers to question everything that follows,' and describes specific techniques used to establish the narrator as unreliable. The primary purpose is to signal that the narrator's account cannot be fully trusted.",
    subtopic: "Text Structure and Purpose"
  },
  {
    id: 29,
    passage: "The scientist's tone in the paper shifts noticeably from the introduction to the conclusion. In the introduction, she is measured and cautious, carefully hedging every claim with qualifiers like 'suggests' and 'may indicate.' By the conclusion, however, her language becomes more ______, as the weight of accumulated evidence allows her to state her findings with greater confidence.",
    question: "Which choice completes the text with the most logical and precise word or phrase?",
    choices: [
      "tentative",
      "assertive",
      "ambiguous",
      "apologetic"
    ],
    correctAnswer: 1,
    explanation: "'Assertive' is correct because the passage describes a shift from cautious hedging to stating findings 'with greater confidence.' Assertive means confident and direct, which contrasts with the earlier measured tone.",
    subtopic: "Words in Context"
  },
  {
    id: 30,
    passage: "Text 1: The widespread use of smartphones has fundamentally altered how people navigate cities. GPS applications have made it possible to travel anywhere without prior knowledge of routes, reducing the cognitive effort required for navigation.\n\nText 2: Neuroscientists have found that heavy reliance on GPS navigation is associated with reduced activity in the hippocampus—the brain region responsible for spatial memory. Studies show that people who regularly use GPS have less developed spatial reasoning skills than those who navigate without technological assistance, raising concerns about long-term cognitive effects.",
    question: "The author of Text 2 would most likely view the development described in Text 1 as",
    choices: [
      "an unambiguous improvement in urban mobility",
      "a convenience that may come with cognitive trade-offs",
      "irrelevant to questions of brain development",
      "a trend that will reverse as people rediscover traditional navigation"
    ],
    correctAnswer: 1,
    explanation: "Text 2 describes how GPS reliance reduces hippocampal activity and spatial reasoning skills, suggesting cognitive costs. The author would view the GPS convenience described in Text 1 as a trade-off—easier navigation at the potential cost of cognitive development.",
    subtopic: "Cross-Text Connections"
  }
];
